import type {
    ChangeProductQuantityInCartMutationVariables,
    ChangeProductSizeInCartMutationVariables,
    ChangeProductSizeToExistOneInCartMutationVariables,
    EccoAddProductToCartMutationVariables,
    EccoRemoveProductFromCartMutationVariables,
    Product as RawProduct,
} from "@graphql/generated/types";
import { activeSiteConfig } from "@config/site/site-config";
import {
    fetchUnavailableProductDetails,
    type UnavailableProductDetails,
} from "@lib/commerce/adapters/commercetools/fetchUnavailableProductDetails";
import { useCommerceToolsClient } from "@lib/commerce/adapters/commercetools/useCommerceToolsClient";
import { getPdpVariantAttributesToInclude } from "@lib/commerce/adapters/commercetools/pdpVariantAttributes";
import { QueryKeys } from "@lib/enums/QueryKeys";
import { useApi } from "@lib/helpers/useApi";
import { LogTag, ServiceType } from "@lib/monitoring/logger";
import { getProductDefaultPrice, normalizeProductData } from "@lib/product/helpers";
import { keepPreviousData } from "@tanstack/react-query";
import { useStore } from "@store/store";
import { updateMutation } from "@store/helpers";
import { useAuth } from "@lib/auth/useAuth";

interface ProductProps {
    sku: string;
    locale: string;
}

export type { UnavailableProductDetails };

export const useAddProductToCart = () => {
    const { client } = useCommerceToolsClient();
    const executeWithVariables = client.eccoAddProductToCart;
    const setData = useStore((state) => state.setCart);
    return updateMutation<EccoAddProductToCartMutationVariables>(executeWithVariables, setData);
};

export const useRemoveProductFromCart = () => {
    const { client } = useCommerceToolsClient();
    const executeWithVariables = client.eccoRemoveProductFromCart;
    const setData = useStore((state) => state.setCart);
    return updateMutation<EccoRemoveProductFromCartMutationVariables>(
        executeWithVariables,
        setData
    );
};

export const useUpdateProductQtyInCart = () => {
    const { client } = useCommerceToolsClient();
    const executeWithVariables = client.changeProductQuantityInCart;
    const setData = useStore((state) => state.setCart);
    return updateMutation<ChangeProductQuantityInCartMutationVariables>(
        executeWithVariables,
        setData
    );
};

export const useUpdateProductSizeInCart = () => {
    const { client } = useCommerceToolsClient();
    const executeWithVariables = client.changeProductSizeInCart;
    const setData = useStore((state) => state.setCart);
    return updateMutation<ChangeProductSizeInCartMutationVariables>(executeWithVariables, setData);
};

export const useUpdateProductSizeToExistOneInCart = () => {
    const { client } = useCommerceToolsClient();
    const executeWithVariables = client.changeProductSizeToExistOneInCart;
    const setData = useStore((state) => state.setCart);
    return updateMutation<ChangeProductSizeToExistOneInCartMutationVariables>(
        executeWithVariables,
        setData
    );
};

export const useProduct = ({ sku, locale }: ProductProps) => {
    const { session } = useAuth();
    const { client } = useCommerceToolsClient();

    const { data, error, isFetching } = useApi.useAsync(
        [QueryKeys.getProduct, sku, locale],
        async () => {
            const rawProduct = await client
                .productDetails({
                    sku,
                    storeKey: process.env.NEXT_PUBLIC_STORE_KEY,
                    channelId: process.env.NEXT_PUBLIC_CHANNEL_ID,
                    distributionChannelWhereClause: `productSelection(id="${process.env.NEXT_PUBLIC_DISTRIBUTION_CHANNEL_ID}")`,
                    currency: activeSiteConfig.currency,
                    country: activeSiteConfig.country,
                    commercetoolsLocale: locale,
                    variantAttributesToInclude: getPdpVariantAttributesToInclude(),
                })
                .then((r) => r?.data?.commercetoolsProduct?.product);

            if (!rawProduct) {
                throw new Error(`Product with SKU ${sku} not found`);
            }
            return normalizeProductData(rawProduct as RawProduct, locale);
        },
        {
            staleTime: 60 * 1000 * 1, // 1 minute
            enabled: !!session?.accessToken,
            placeholderData: keepPreviousData,
            meta: {
                serviceType: ServiceType.COMMERCE_TOOLS,
                message: `useProduct: Issue loading product with id: ${sku} for locale ${locale}`,
                tag: LogTag.PRODUCT,
                payload: { sku, locale },
            },
        }
    );

    const defaultPrice = getProductDefaultPrice(data);

    return { product: data, defaultPrice, error, isFetching };
};

interface ProductsBySkusProps {
    skus: string[];
    locale: string;
}

export const useProductsBySkus = ({ skus, locale }: ProductsBySkusProps) => {
    const { session } = useAuth();
    const { client } = useCommerceToolsClient();

    const { data, error, isFetching } = useApi.useAsync(
        [QueryKeys.getMultipleProducts, skus, locale],
        async () => {
            const results = await client
                .getMultipleProducts({
                    skus,
                    locale,
                    localeProjection: [locale],
                    channelId: process.env.NEXT_PUBLIC_CHANNEL_ID,
                    currency: activeSiteConfig.currency,
                    country: activeSiteConfig.country,
                    distributionChannelWhereClause: `productSelection(id="${process.env.NEXT_PUBLIC_DISTRIBUTION_CHANNEL_ID}")`,
                })
                .then((r) => r.data.products.results);

            const availableInMarket = results.filter(
                (rawProduct) =>
                    rawProduct.productSelectionRefs.exists &&
                    (rawProduct.masterData.current?.variants.length ?? 0) > 0
            );
            return availableInMarket.map((rawProduct) =>
                normalizeProductData(rawProduct as RawProduct, locale)
            );
        },
        {
            staleTime: 60 * 1000 * 30, // 30 minutes
            enabled: !!session?.accessToken && skus.length > 0,
            placeholderData: keepPreviousData,
            meta: {
                serviceType: ServiceType.COMMERCE_TOOLS,
                message: `useProductsBySkus: Issue loading products with skus: ${skus.join(", ")} for locale ${locale}`,
                tag: LogTag.PRODUCT,
                payload: { skus, locale },
            },
        }
    );
    return { products: data || [], error, isFetching };
};

export const useUnavailableProductDetails = ({
    skus,
    locale,
}: {
    skus: string[];
    locale: string;
}) => {
    const { status } = useAuth();
    const { baseClient } = useCommerceToolsClient();
    const { data, error, isFetching } = useApi.useAsync(
        [QueryKeys.getUnavailableProductDetails, skus, locale],
        () => fetchUnavailableProductDetails(baseClient, { skus, locale }),
        {
            meta: {
                serviceType: ServiceType.COMMERCE_TOOLS,
                message: "useUnavailableProductDetails: Issue loading unavailable product details",
                tag: LogTag.SHOPPING_BAG,
                payload: { skus, locale },
            },
            enabled: skus.length > 0 && status === "authenticated",
        }
    );

    return {
        detailsBySku: data ?? new Map<string, UnavailableProductDetails>(),
        error,
        isFetching,
    };
};
